import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Users, Copy, Gift, Trophy, Share2, MessageCircle, Sparkles, Check, Mail, ChevronRight } from "lucide-react";

export default function ReferPage() {
  const [email, setEmail] = useState(() => localStorage.getItem("in21_ref_email") || "");
  const [name, setName] = useState(() => localStorage.getItem("in21_ref_name") || "");
  const [stats, setStats] = useState(null);
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async (em) => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get(`/referrals/stats?email=${encodeURIComponent(em)}`),
        api.get("/referrals/leaderboard?limit=10"),
      ]);
      setStats(s.data); setBoard(l.data);
    } catch (e) { setStats(null); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/referrals/leaderboard?limit=10").then((r) => setBoard(r.data)).catch(() => {});
    if (email) load(email);
    // eslint-disable-next-line
  }, []);

  const generate = async () => {
    if (!email.trim() || !email.includes("@")) return toast.error("Valid email दर्ज करें");
    setLoading(true);
    try {
      await api.post("/referrals/generate-code", { email, name });
      localStorage.setItem("in21_ref_email", email);
      if (name) localStorage.setItem("in21_ref_name", name);
      toast.success("आपका invite code तैयार है!");
      await load(email);
    } catch (e) { toast.error("Code generate नहीं हो सका"); }
    finally { setLoading(false); }
  };

  const copyLink = () => {
    if (!stats?.share_url) return;
    navigator.clipboard.writeText(stats.share_url);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
    toast.success("Link copied!");
  };

  const shareWA = () => {
    if (!stats?.share_url) return;
    const msg = `नमस्ते! 🙏\n\nINDIAN-NEWS-21 पर छत्तीसगढ़ की सबसे ताज़ा हिंदी खबरें पढ़ें — ब्रेकिंग न्यूज़, राजनीति, अपराध, खेल, सब कुछ एक ही जगह।\n\nमेरे invite link से join करें:\n${stats.share_url}\n\n📰 भारत की आवाज़`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const nativeShare = async () => {
    if (!stats?.share_url) return;
    if (navigator.share) {
      try { await navigator.share({ title: "INDIAN-NEWS-21", text: "छत्तीसगढ़ की सबसे ताज़ा खबरें", url: stats.share_url }); } catch {}
    } else copyLink();
  };

  const progressPct = stats?.next_tier
    ? Math.min(100, Math.round((stats.referrals_count / stats.next_tier.min_referrals) * 100))
    : 100;

  return (
    <>
      <PublicHeader />
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white">
        <div className="absolute inset-0 opacity-15" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #EA580C 0, transparent 50%), radial-gradient(circle at 80% 70%, #DC2626 0, transparent 50%)" }}/>
        <div className="max-w-6xl mx-auto px-4 py-10 relative z-10">
          <div className="inline-flex items-center gap-2 bg-amber-400/20 text-amber-300 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest mb-3">
            <Gift className="w-3 h-3"/>Refer & Earn · 100% Legal
          </div>
          <h1 className="font-devnagri-heading text-3xl md:text-5xl font-black leading-tight">दोस्तों को Invite करो, Rewards पाओ 🎁</h1>
          <p className="text-amber-200/90 mt-2 text-sm md:text-base max-w-3xl">जितने ज़्यादा लोग आपके link से INDIAN-NEWS-21 join करेंगे, उतने बड़े rewards — Ad-free, HD E-Paper Download, INDIAN-NEWS-21 mug, यहाँ तक कि Chief Editor JAKIR MOMIN से 15-min WhatsApp call!</p>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 py-8 pb-24" data-testid="refer-page">
        {!stats ? (
          <div className="max-w-md mx-auto rounded-2xl border-2 border-slate-100 bg-white p-6 shadow-md">
            <h2 className="font-devnagri-heading text-xl font-black text-[#0F172A] mb-1">अपना Invite Code बनाओ</h2>
            <p className="text-sm text-slate-500 mb-4">1 minute में — कोई signup नहीं, बस email।</p>
            <div className="space-y-3">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="आपका नाम (optional)" className="w-full px-3 py-2.5 rounded-lg border-2 border-slate-200 text-sm outline-none focus:border-[#DC2626]" data-testid="refer-name"/>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-3.5 text-slate-400"/>
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full pl-10 pr-3 py-2.5 rounded-lg border-2 border-slate-200 text-sm outline-none focus:border-[#DC2626]" data-testid="refer-email"/>
              </div>
              <button onClick={generate} disabled={loading} className="w-full py-3 rounded-xl bg-gradient-to-r from-[#DC2626] via-[#EA580C] to-[#F59E0B] hover:from-[#B91C1C] text-white font-black text-sm shadow-lg" data-testid="refer-generate">
                {loading ? "Loading…" : "🎯 मेरा Invite Code बनाओ"}
              </button>
              <div className="text-[11px] text-slate-500 text-center">आपका email कहीं public नहीं होगा · कोई spam नहीं</div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Share widget */}
            <div className="lg:col-span-2 space-y-4">
              <div className="rounded-2xl bg-gradient-to-br from-[#DC2626] via-[#EA580C] to-[#F59E0B] p-6 text-white shadow-2xl" data-testid="refer-widget">
                <div className="text-[10px] uppercase tracking-widest font-black text-amber-100 mb-1">आपका unique invite code</div>
                <div className="font-mono text-3xl font-black tracking-wider mb-3" data-testid="refer-code">{stats.code}</div>
                <div className="flex items-center gap-2 bg-black/25 backdrop-blur rounded-lg p-3">
                  <div className="min-w-0 flex-1 text-xs break-all opacity-95">{stats.share_url}</div>
                  <button onClick={copyLink} className="shrink-0 flex items-center gap-1 bg-white text-[#DC2626] rounded px-3 py-1.5 text-xs font-black hover:bg-amber-50" data-testid="refer-copy">
                    {copied ? <><Check className="w-3.5 h-3.5"/>Copied</> : <><Copy className="w-3.5 h-3.5"/>Copy</>}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button onClick={shareWA} className="flex items-center justify-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-black py-2.5" data-testid="refer-wa"><MessageCircle className="w-4 h-4"/>WhatsApp Share</button>
                  <button onClick={nativeShare} className="flex items-center justify-center gap-1.5 rounded-lg bg-white/20 hover:bg-white/30 backdrop-blur text-white text-sm font-black py-2.5" data-testid="refer-share"><Share2 className="w-4 h-4"/>More options</button>
                </div>
              </div>

              {/* Progress + Current tier */}
              <div className="rounded-2xl bg-white border-2 border-slate-100 p-5">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-black text-slate-500">आपका current level</div>
                    <div className="font-devnagri-heading text-xl font-black text-[#0F172A]">{stats.current_tier.label}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-black text-3xl text-[#DC2626] tabular-nums">{stats.referrals_count}</div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">friends joined</div>
                  </div>
                </div>
                {stats.next_tier && (
                  <>
                    <div className="h-3 rounded-full bg-slate-100 overflow-hidden mt-3">
                      <div className="h-full bg-gradient-to-r from-[#DC2626] via-[#EA580C] to-[#F59E0B] transition-all duration-500" style={{ width: `${progressPct}%` }}/>
                    </div>
                    <div className="text-xs text-slate-600 mt-2">
                      <b>{stats.next_needed}</b> और invite ⇒ अगला level <b>"{stats.next_tier.label}"</b> unlock होगा
                    </div>
                  </>
                )}
              </div>

              {/* Tier ladder */}
              <div className="rounded-2xl bg-white border-2 border-slate-100 p-5">
                <div className="font-devnagri-heading text-lg font-black text-[#0F172A] mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500"/>सारे Rewards</div>
                <ul className="space-y-2">
                  {[
                    { r: 1, l: "🌱 Newbie Ambassador", p: "तेज़ पेज लोड · No signup wall" },
                    { r: 3, l: "🥉 Bronze Reader", p: "Ad-free reading · 1 महीना" },
                    { r: 5, l: "🥈 Silver Reader", p: "Ad-free · 3 महीने + HD E-Paper · 3 महीने" },
                    { r: 10, l: "🥇 Gold Reader", p: "Ad-free · 12 महीने + HD E-Paper 12 महीने + INDIAN-NEWS-21 mug" },
                    { r: 25, l: "💎 Diamond Reader", p: "Lifetime Ad-free + HD E-Paper + Feature story + JAKIR MOMIN से 15-min WhatsApp call" },
                  ].map((t) => {
                    const done = stats.referrals_count >= t.r;
                    return (
                      <li key={t.r} className={`flex items-center gap-3 p-3 rounded-lg border ${done ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200"}`}>
                        <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-black text-sm ${done ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"}`}>{done ? "✓" : t.r}</div>
                        <div className="min-w-0 flex-1">
                          <div className="font-black text-sm text-[#0F172A]">{t.l}</div>
                          <div className="text-[11px] text-slate-600">{t.p}</div>
                        </div>
                        {done && <div className="text-[9px] font-black uppercase tracking-widest bg-emerald-600 text-white px-1.5 py-0.5 rounded">Unlocked</div>}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {stats.recent?.length > 0 && (
                <div className="rounded-2xl bg-white border-2 border-slate-100 p-5">
                  <div className="font-devnagri-heading text-lg font-black text-[#0F172A] mb-3">हाल के invites</div>
                  <ul className="divide-y divide-slate-100">
                    {stats.recent.map((r, i) => (
                      <li key={i} className="py-2 flex items-center justify-between text-sm">
                        <span className="text-slate-700">{r.new_name || "अनाम"} <span className="text-[11px] text-slate-500">· {r.source}</span></span>
                        <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-600">{r.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Right: Leaderboard */}
            <aside className="space-y-4">
              <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-[#0F172A] text-white p-5" data-testid="refer-leaderboard">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="w-5 h-5 text-amber-400"/>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-black text-amber-300">Top Referrers</div>
                    <div className="font-devnagri-heading text-lg font-black">इस महीने के champions</div>
                  </div>
                </div>
                {board.length === 0 ? <div className="text-sm text-slate-400 italic">पहले बनने का मौक़ा — invite शुरू करें!</div> :
                <ul className="space-y-2">
                  {board.map((b) => (
                    <li key={b.rank} className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center font-black text-xs text-amber-300">{b.rank}</div>
                      <div className="min-w-0 flex-1 truncate">
                        <div className="font-bold truncate">{b.name}</div>
                        <div className="text-[10px] text-slate-400 truncate">{b.email_masked} · {b.tier}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-black text-amber-300 tabular-nums">{b.count}</div>
                      </div>
                    </li>
                  ))}
                </ul>}
              </div>
              <div className="rounded-xl bg-amber-50 border-2 border-amber-200 p-4 text-xs text-amber-900">
                <div className="font-black mb-1 flex items-center gap-1"><Users className="w-3.5 h-3.5"/>यह कैसे काम करता है</div>
                <ol className="space-y-1 list-decimal list-inside ml-1">
                  <li>अपना unique link WhatsApp पर share करें</li>
                  <li>जब कोई link से INDIAN-NEWS-21 पर newsletter subscribe करे, count बढ़ेगा</li>
                  <li>Threshold पूरा होते ही reward automatically unlock</li>
                  <li>25 invites पर Boss से खुद call — <b>यह भारत का सबसे बड़ा reader-first news portal</b></li>
                </ol>
              </div>
            </aside>
          </div>
        )}
      </main>
      <PublicFooter />
    </>
  );
}
